<template>
  <div class="billing-transfer-report-page">
    <h6>Repasses dos profissionais</h6>
    <div class="columns" style="margin-bottom: 1rem;">
      <div class="column col-6 col-md-6 col-sm-12 form-group" :class="{'has-error': periodError}">
        <label class="form-label">{{ periodLabel }}</label>
        <div class="report-period">
          <div class="btn-group">
            <button class="btn btn-sm"
                    :class="filters.mode === 'competence' ? 'btn-primary' : 'btn-gray'"
                    @click="filters.mode = 'competence'">Competência</button>
            <button class="btn btn-sm"
                    :class="filters.mode === 'period' ? 'btn-primary' : 'btn-gray'"
                    @click="filters.mode = 'period'">Período</button>
          </div>
          <input type="text" class="form-input report-competence"
                 v-if="filters.mode === 'competence'"
                 v-model="filters.competence" v-mask="'00/0000'" placeholder="00/0000"
                 @blur="$v.filters.competence.$touch()">
          <st-date-period v-else
            :start-date.sync="filters.startDate"
            :end-date.sync="filters.endDate"
            :max-months="6"
            :start-date-validator="$v.filters.startDate"
            :end-date-validator="$v.filters.endDate"
            @blur:startDate="$v.filters.startDate.$touch()"
            @blur:endDate="$v.filters.endDate.$touch()"
          />
        </div>
        <div class="form-input-hint"
             v-if="filters.mode === 'competence' && $v.filters.competence.$error">
          {{ competenceHint }}
        </div>
      </div>
      <div class="column col-6 col-md-6 col-sm-12 form-group">
        <label class="form-label">Profissional</label>
        <select id="report-professional" class="form-select" v-model="filters.professionalId">
          <option value="">[Todos os profissionais]</option>
          <option v-for="item in professionals" :value="item.id" :key="item.id">
            {{ item.name }}
          </option>
        </select>
      </div>
      <div class="column col-6 col-md-6 col-sm-12 form-group">
        <label class="form-label">Convênio</label>
        <select id="report-insurance" class="form-select" v-model="filters.planId">
          <option value="">[Todos os convênios]</option>
          <option v-for="item in insurances" :value="item.id" :key="item.id">
            {{ item.name }}
          </option>
        </select>
      </div>
      <div class="column col-3 col-md-6 col-sm-12 form-group">
        <label class="form-label">Origem</label>
        <select id="report-origin" class="form-select" v-model="filters.origin">
          <option value="all">Todas as origens</option>
          <option v-for="item in originItems" :value="item.value" :key="item.value">
            {{ item.label }}
          </option>
        </select>
      </div>
      <div class="column col-3 col-md-6 col-sm-12 form-group"
           :class="{'has-error': $v.filters.statuses.$error}">
        <label class="form-label">Status</label>
        <st-multi-select v-model="filters.statuses" :items="statusItems"
                         value-key="value" label-key="label"/>
        <div class="form-input-hint" v-if="$v.filters.statuses.$error">Campo obrigatório</div>
      </div>
      <div class="column col-4 col-md-6 col-sm-12 form-group">
        <label class="form-label">Agrupado por</label>
        <select id="report-group" class="form-select" v-model="filters.groupBy">
          <option value="professional">Profissional</option>
          <option value="insurance">Convênio</option>
          <option value="expense">Serviço</option>
        </select>
      </div>
    </div>
    <div class="footer">
      <button class="btn btn-gray btn-icon-left mr-1"
              :class="{loading: running === 'xlsx'}" :disabled="!!running" @click="run('xlsx')">
        <fa-icon :icon="['fal', 'file-excel']"></fa-icon>Gerar planilha
      </button>
      <button class="btn btn-primary btn-icon-left"
              :class="{loading: running === 'pdf'}" :disabled="!!running" @click="run('pdf')">
        <fa-icon :icon="['fal', 'print']"></fa-icon>Imprimir
      </button>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { required, requiredIf } from 'vuelidate/lib/validators';
import {
  date, minDate, maxDate, competenceDate,
} from '@/data/validators';
import { origins } from '@/data/billing-transfer-origins';
import { statuses } from '@/data/billing-transfer-statuses';
import * as professionalApi from '@/app/professional/api';

const REPORT_FILE = 'repasses-profissionais.xlsx';

export default {
  data() {
    const previous = moment().subtract(1, 'month');
    return {
      running: null,
      professionals: [],
      insurances: [],
      filters: {
        mode: 'competence',
        competence: previous.format('MM/YYYY'),
        startDate: previous.clone().startOf('month').format('YYYY-MM-DD'),
        endDate: previous.clone().endOf('month').format('YYYY-MM-DD'),
        professionalId: '',
        planId: '',
        origin: 'all',
        statuses: ['pending', 'consolidated', 'paid'],
        groupBy: 'professional',
      },
    };
  },
  validations() {
    const isPeriod = this.filters.mode === 'period';
    const { startDate } = this.filters;
    const validStart = startDate && moment(startDate, 'YYYY-MM-DD', true).isValid();
    return {
      filters: {
        statuses: { required },
        competence: {
          required: requiredIf(() => !isPeriod),
          competenceDate,
        },
        startDate: {
          required: requiredIf(() => isPeriod),
          date,
        },
        endDate: {
          required: requiredIf(() => isPeriod),
          date,
          minDate: minDate(isPeriod && validStart ? startDate : null),
          maxDate: maxDate(isPeriod && validStart ? moment(startDate).add(6, 'months') : null),
        },
      },
    };
  },
  computed: {
    originItems() {
      return Object.keys(origins).map(value => ({ value, label: origins[value] }));
    },
    statusItems() {
      return Object.keys(statuses).map(value => ({ value, label: statuses[value] }));
    },
    periodLabel() {
      return this.filters.mode === 'period' ? 'Período (até 6 meses)' : 'Competência';
    },
    competenceHint() {
      return this.$v.filters.competence.required === false
        ? 'Campo obrigatório'
        : 'Competência inválida';
    },
    periodError() {
      if (this.filters.mode === 'competence') return this.$v.filters.competence.$error;
      return this.$v.filters.startDate.$error || this.$v.filters.endDate.$error;
    },
  },
  mounted() {
    this.loadProfessionals();
    this.loadInsurances();
  },
  methods: {
    loadProfessionals() {
      professionalApi.allActive()
        .then((data) => {
          this.professionals = data.items;
        })
        .catch(() => {});
    },
    loadInsurances() {
      this.$http.get('/insurance-plans', { params: { limit: 0, active: true } })
        .then(({ data }) => {
          this.insurances = data.items.map(item => ({ id: item.plan.id, name: item.customName }));
        })
        .catch(() => {});
    },
    params(format) {
      const params = {
        format,
        groupBy: this.filters.groupBy,
        origin: this.filters.origin,
        status: this.filters.statuses.join(','),
      };
      if (this.filters.mode === 'period') {
        params.startDate = this.filters.startDate;
        params.endDate = this.filters.endDate;
      } else {
        const competence = moment(`01/${this.filters.competence}`, 'DD/MM/YYYY', true);
        params.month = competence.month() + 1;
        params.year = competence.year();
      }
      if (this.filters.professionalId) params.professionalId = this.filters.professionalId;
      if (this.filters.planId) params.planId = this.filters.planId;
      return params;
    },
    run(format) {
      this.$v.filters.$touch();
      if (this.$v.filters.$error) return;

      this.running = format;
      const params = this.params(format);
      const request = format === 'xlsx'
        ? this.$file.download('/billing-transfers/report', params, { name: REPORT_FILE })
        : this.$file.print('/billing-transfers/report', params);
      Promise.resolve(request)
        .catch(() => {})
        .finally(() => {
          this.running = null;
        });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.billing-transfer-report-page {
  .report-period {
    display: flex;
    gap: $layout-spacing-sm;
    .btn-group .btn {
      height: 1.4rem;
    }
    .report-competence {
      text-align: center;
      width: 4.6rem;
    }
  }
  .footer {
    margin-top: $layout-spacing-lg;
    text-align: right;
  }
}
</style>
